// Player-facing spell text — split out from shared/config.ts (which stays
// purely numeric balance data) since this is presentation content: the
// top-left HUD's per-spell description line (src/render.ts's renderHud) and
// the "how do I cast this" instruction that follows the cursor once a spell
// is armed (src/render.ts's drawSpellCastHint). Both read from here so the
// wording only lives in one place.

import type { HeroClass, SpellSlot } from "./types";

/** One-line description of what each class's 3 spells do — mirrors the comments next to CONFIG.heroSpells in shared/config.ts. */
export const HERO_SPELL_DESCRIPTIONS: Record<HeroClass, Record<SpellSlot, string>> = {
  warrior: {
    1: "AOE damage over time around you.",
    2: "Self buff: reduced damage taken, increased attack rate.",
    3: "Leap toward the cursor; can cross void.",
  },
  hunter: {
    1: "Piercing skill shot in a line toward the cursor.",
    2: "Self buff: increased move speed and attack range.",
    3: "Target a hero: refreshes their spell 1 cooldown.",
  },
  mage: {
    1: "Glyph at the cursor: damage over time to enemies in it.",
    2: "Teleport yourself and nearby heroes to the cursor.",
    3: "Glyph at the cursor: heal over time to heroes in it.",
  },
};

/**
 * What a click actually needs to land on for the armed-spell instruction
 * text (src/input.ts arms a spell on the first keypress; a click then
 * confirms it against the caster's current aim) — "hero" for the one spell
 * that resolves against the closest hero to the pointer (Hunter's spell 3),
 * "anywhere" for every other spell (self-buffs/AOEs/leaps all just resolve
 * against wherever you clicked, no specific target required).
 */
export const HERO_SPELL_CLICK_TARGET: Record<HeroClass, Record<SpellSlot, "anywhere" | "hero">> = {
  warrior: { 1: "anywhere", 2: "anywhere", 3: "anywhere" },
  hunter: { 1: "anywhere", 2: "anywhere", 3: "hero" },
  mage: { 1: "anywhere", 2: "anywhere", 3: "anywhere" },
};

/** The two-line instruction shown following the cursor while a spell is armed, waiting for the confirm click — see src/render.ts's drawSpellCastHint. */
export function spellCastHintLines(heroClass: HeroClass, slot: SpellSlot, cancelKeyLabel: string): [string, string] {
  const target = HERO_SPELL_CLICK_TARGET[heroClass][slot];
  const clickLine = target === "hero" ? "Click any hero to cast." : "Click anywhere to cast.";
  return [clickLine, `Press ${cancelKeyLabel} again to cancel.`];
}
